import * as React from 'react';
import { CollapsibleCard, Tooltip, cn } from '@insolech/ui';
import { Info } from 'lucide-react';
import type {
  MidfootState,
  ArchHeightDirection,
  FascialAccommodationOption,
  MlaExtraFilledOption,
} from './prescriptionState';
import {
  ModCard,
  SideRow,
  SelectSm,
  NumInput,
  FieldLabel,
  SectionObservation,
} from './shared';
import { CARD_IDS } from './cardIds';

/**
 * MidfootSection (R29 rebuild).
 *
 * Card order:
 *   1. Arch Height Adjustment (full width - MLA + LLA, per side)
 *   2. Fascial accommodation (Select per side, optional depth)
 *   3. MLA extra filled (Yes/No per side, fixed 3mm red PORON 12° spec)
 *   4. Medial flange (Yes/No per side)
 *   5. Lateral flange (Yes/No per side)
 *   6. Arch reinforcement (full width - MLA + LLA, per side; merged with
 *      the previous Posted external arch card)
 *
 * Images live at apps/web/public/prescription/modifications/<name>.png.
 */

interface Props {
  value: MidfootState;
  onChange: (next: MidfootState) => void;
  /** Sandal-only branch (R37). When true, hides Medial flange, Lateral
   *  flange and Arch reinforcement cards - the sandal form keeps only
   *  Arch Height Adjustment, Fascial accommodation, MLA extra filled. */
  isSandal?: boolean | undefined;
  readOnly?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  open?: boolean | undefined;
  onOpenChange?: ((next: boolean) => void) | undefined;
}

export const ARCH_HEIGHT_OPTIONS: ReadonlyArray<{ value: ArchHeightDirection; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'increase', label: 'Increase' },
  { value: 'decrease', label: 'Decrease' },
];

export const FASCIAL_OPTIONS: ReadonlyArray<{ value: FascialAccommodationOption; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'yes', label: 'Yes' },
  { value: 'yes_poron', label: 'Yes + Poron' },
];

export const MLA_EXTRA_FILLED_OPTIONS: ReadonlyArray<{
  value: MlaExtraFilledOption;
  label: string;
}> = [
  { value: 'none', label: 'None' },
  { value: 'poron_3mm_12deg', label: 'With 3mm Poron' },
];

/** Default fascial depth applied when the user picks one of the Yes options. */
const FASCIAL_DEFAULT_DEPTH_MM = -3;

export function MidfootSection({
  value,
  onChange,
  isSandal = false,
  readOnly,
  defaultOpen = false,
  open,
  onOpenChange,
}: Props) {
  const set = <K extends keyof MidfootState>(k: K, v: MidfootState[K]) =>
    onChange({ ...value, [k]: v });

  const aha = value.arch_height_adjustment ?? {};
  const fa = value.fascial_accommodation ?? {};
  const mef = value.mla_extra_filled ?? {};
  const mf = value.medial_flange ?? {};
  const lf = value.lateral_flange ?? {};
  const ar = value.arch_reinforcement ?? {};

  const isControlled = open !== undefined && onOpenChange !== undefined;
  return (
    <CollapsibleCard
      title="Midfoot modifications"
      subtitle="Arch height, fascia, MLA fill, flanges, arch reinforcement"
      {...(isControlled
        ? { open: open!, onOpenChange: onOpenChange! }
        : { defaultOpen })}
    >
      <div className="space-y-3">
        {/* 1. Arch Height Adjustment - full row, MLA + LLA side-by-side */}
        <DualColumnCard
          id={CARD_IDS.archHeightAdjustment}
          title="Arch Height Adjustment"
          imageSrc="/prescription/modifications/arch-height-adjustment.png"
          imageAlt="Arch height adjustment reference"
          info={
            <div className="max-w-[280px] text-xs leading-snug space-y-1">
              <p>Pick the direction and dial in the height in mm (1-9).</p>
              <p>
                <strong>Increase</strong> raises the arch (more support).
                <br />
                <strong>Decrease</strong> lowers it (less support).
              </p>
            </div>
          }
          left={
            <ArchHeightColumn
              label="Medial Longitudinal Arch (MLA)"
              data={{
                left_level: aha.mla_left_level,
                left_height_mm: aha.mla_left_height_mm,
                right_level: aha.mla_right_level,
                right_height_mm: aha.mla_right_height_mm,
              }}
              onPatch={(p) => {
                const remapped: Parameters<typeof set>[1] = { ...aha };
                if ('left_level' in p) remapped.mla_left_level = p.left_level;
                if ('left_height_mm' in p) remapped.mla_left_height_mm = p.left_height_mm;
                if ('right_level' in p) remapped.mla_right_level = p.right_level;
                if ('right_height_mm' in p) remapped.mla_right_height_mm = p.right_height_mm;
                set('arch_height_adjustment', remapped);
              }}
              readOnly={readOnly}
            />
          }
          right={
            <ArchHeightColumn
              label="Lateral Longitudinal Arch (LLA)"
              data={{
                left_level: aha.lla_left_level,
                left_height_mm: aha.lla_left_height_mm,
                right_level: aha.lla_right_level,
                right_height_mm: aha.lla_right_height_mm,
              }}
              onPatch={(p) => {
                const remapped: Parameters<typeof set>[1] = { ...aha };
                if ('left_level' in p) remapped.lla_left_level = p.left_level;
                if ('left_height_mm' in p) remapped.lla_left_height_mm = p.left_height_mm;
                if ('right_level' in p) remapped.lla_right_level = p.right_level;
                if ('right_height_mm' in p) remapped.lla_right_height_mm = p.right_height_mm;
                set('arch_height_adjustment', remapped);
              }}
              readOnly={readOnly}
            />
          }
        />

        {/* Row B: Fascial accommodation (55%) + MLA extra filled (45%) */}
        <div className="grid grid-cols-1 md:grid-cols-[11fr_9fr] gap-3">
          {/* 2. Fascial accommodation - 55% */}
          <div>
            <ModCard
              id={CARD_IDS.fascialAccommodation}
              title="Fascial accommodation"
              subtitle="Plantar fascia accommodation per side."
              image={{
                src: '/prescription/modifications/fascial-accommodation.png',
                alt: 'Fascial accommodation reference',
              }}
            >
              <FascialRow
                side="L"
                option={fa.left}
                depth={fa.left_depth_mm}
                onChange={(p) => {
                  set('fascial_accommodation', {
                    ...fa,
                    ...(p.option !== undefined ? { left: p.option } : {}),
                    ...(p.depth !== undefined ? { left_depth_mm: p.depth } : {}),
                  });
                }}
                readOnly={readOnly}
              />
              <FascialRow
                side="R"
                option={fa.right}
                depth={fa.right_depth_mm}
                onChange={(p) => {
                  set('fascial_accommodation', {
                    ...fa,
                    ...(p.option !== undefined ? { right: p.option } : {}),
                    ...(p.depth !== undefined ? { right_depth_mm: p.depth } : {}),
                  });
                }}
                readOnly={readOnly}
              />
            </ModCard>
          </div>

          {/* 3. MLA extra filled - 45% */}
          <div>
            <ModCard
              id={CARD_IDS.mlaExtraFilled}
              title="MLA extra filled"
              subtitle="For extra MLA comfort."
              image={{
                src: '/prescription/modifications/mla-extra-filled.png',
                alt: 'MLA extra filled reference',
              }}
            >
              <SideRow side="L">
                <SelectSm
                  value={mef.left}
                  onChange={(v) => set('mla_extra_filled', { ...mef, left: v })}
                  options={MLA_EXTRA_FILLED_OPTIONS}
                  disabled={readOnly}
                  placeholder="Select option"
                  className="min-w-[9rem]"
                />
              </SideRow>
              <SideRow side="R">
                <SelectSm
                  value={mef.right}
                  onChange={(v) => set('mla_extra_filled', { ...mef, right: v })}
                  options={MLA_EXTRA_FILLED_OPTIONS}
                  disabled={readOnly}
                  placeholder="Select option"
                  className="min-w-[9rem]"
                />
              </SideRow>
            </ModCard>
          </div>
        </div>

        {/* Row C: Medial flange + Lateral flange (50/50).
            Hidden entirely for sandals (R37). */}
        {!isSandal ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* 4. Medial flange - Yes/No per side */}
          <ModCard
            id={CARD_IDS.medialFlange}
            title="Medial flange"
            subtitle="Medial wall extending up the foot."
            image={{
              src: '/prescription/modifications/medial-flange.png',
              alt: 'Medial flange reference',
            }}
          >
            <SideRow side="L">
              <YesNoToggle
                value={mf.left_enabled ?? false}
                onChange={(b) => set('medial_flange', { ...mf, left_enabled: b })}
                disabled={readOnly}
              />
            </SideRow>
            <SideRow side="R">
              <YesNoToggle
                value={mf.right_enabled ?? false}
                onChange={(b) => set('medial_flange', { ...mf, right_enabled: b })}
                disabled={readOnly}
              />
            </SideRow>
          </ModCard>

          {/* 5. Lateral flange - Yes/No per side */}
          <ModCard
            id={CARD_IDS.lateralFlange}
            title="Lateral flange"
            subtitle="Lateral wall extending up the foot."
            image={{
              src: '/prescription/modifications/lateral-flange.png',
              alt: 'Lateral flange reference',
            }}
          >
            <SideRow side="L">
              <YesNoToggle
                value={lf.left_enabled ?? false}
                onChange={(b) => set('lateral_flange', { ...lf, left_enabled: b })}
                disabled={readOnly}
              />
            </SideRow>
            <SideRow side="R">
              <YesNoToggle
                value={lf.right_enabled ?? false}
                onChange={(b) => set('lateral_flange', { ...lf, right_enabled: b })}
                disabled={readOnly}
              />
            </SideRow>
          </ModCard>
        </div>
        ) : null}

        {/* 6. Arch reinforcement (full width, MLA + LLA columns) - merged
            with the legacy Posted external arch card.
            Hidden for sandals (R37). */}
        {!isSandal ? (
        <DualColumnCard
          id={CARD_IDS.archReinforcement}
          title="Arch reinforcement"
          imageSrc="/prescription/modifications/arch-reinforcement.png"
          imageAlt="Arch reinforcement reference"
          left={
            <ReinforcementColumn
              label="Medial Longitudinal Arch (MLA)"
              // Read the boolean keys; fall back to the legacy enum so a
              // draft saved before the toggle refactor still summarises.
              leftValue={
                ar.mla_left_enabled ??
                (ar.mla_left_level && ar.mla_left_level !== 'none' ? true : undefined)
              }
              rightValue={
                ar.mla_right_enabled ??
                (ar.mla_right_level && ar.mla_right_level !== 'none' ? true : undefined)
              }
              onChange={(p) => {
                set('arch_reinforcement', {
                  ...ar,
                  ...(p.left !== undefined ? { mla_left_enabled: p.left } : {}),
                  ...(p.right !== undefined ? { mla_right_enabled: p.right } : {}),
                });
              }}
              readOnly={readOnly}
            />
          }
          right={
            <ReinforcementColumn
              label="Lateral Longitudinal Arch (LLA)"
              leftValue={
                ar.lla_left_enabled ??
                (ar.lla_left_level && ar.lla_left_level !== 'none' ? true : undefined)
              }
              rightValue={
                ar.lla_right_enabled ??
                (ar.lla_right_level && ar.lla_right_level !== 'none' ? true : undefined)
              }
              onChange={(p) => {
                set('arch_reinforcement', {
                  ...ar,
                  ...(p.left !== undefined ? { lla_left_enabled: p.left } : {}),
                  ...(p.right !== undefined ? { lla_right_enabled: p.right } : {}),
                });
              }}
              readOnly={readOnly}
            />
          }
        />
        ) : null}

        <SectionObservation
          value={value.observations}
          onChange={(v) => onChange({ ...value, observations: v })}
          disabled={readOnly}
          placeholder="Section-specific notes for midfoot…"
        />
      </div>
    </CollapsibleCard>
  );
}

// ---- Dual-column card shell (Arch height + Arch reinforcement share this) ----

interface DualColumnCardProps {
  title: string;
  imageSrc: string;
  imageAlt: string;
  info?: React.ReactNode | undefined;
  left: React.ReactNode;
  right: React.ReactNode;
  /** DOM id for the prescription assistant's chip scroll-to-card. */
  id?: string | undefined;
}

function DualColumnCard({
  title,
  imageSrc,
  imageAlt,
  info,
  left,
  right,
  id,
}: DualColumnCardProps) {
  return (
    <div
      id={id}
      className="h-full rounded-md border border-border-subtle bg-white p-3 scroll-mt-32"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-start">
        <div className="space-y-3 min-w-0">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-text-primary leading-tight">{title}</div>
            </div>
            {info ? (
              <Tooltip content={info} side="left">
                <button
                  type="button"
                  aria-label="More info"
                  className="shrink-0 text-text-muted hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-full"
                >
                  <Info className="h-4 w-4" aria-hidden="true" />
                </button>
              </Tooltip>
            ) : null}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
            {left}
            {right}
          </div>
        </div>
        <div className="hidden lg:flex items-center justify-center w-16 h-16 rounded-md bg-sand-50 shrink-0 overflow-hidden self-start">
          <img
            src={imageSrc}
            alt={imageAlt}
            className="h-full w-full object-contain p-1"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
}

// ---- Arch height column (used inside Arch Height Adjustment) ------------

interface ArchHeightColumnProps {
  label: string;
  data: {
    left_level?: ArchHeightDirection | undefined;
    left_height_mm?: number | undefined;
    right_level?: ArchHeightDirection | undefined;
    right_height_mm?: number | undefined;
  };
  onPatch: (p: {
    left_level?: ArchHeightDirection | undefined;
    left_height_mm?: number | undefined;
    right_level?: ArchHeightDirection | undefined;
    right_height_mm?: number | undefined;
  }) => void;
  readOnly?: boolean | undefined;
}

function ArchHeightColumn({
  label,
  data,
  onPatch,
  readOnly,
}: ArchHeightColumnProps) {
  return (
    <div className="space-y-2">
      <FieldLabel>{label}</FieldLabel>
      <SideRow side="L">
        <SelectSm
          value={data.left_level}
          onChange={(v) => {
            // Switching to None clears any height the operator already typed
            // so stale values can't survive in state (or land in the order).
            if (!v || v === 'none') {
              onPatch({ left_level: v, left_height_mm: undefined });
            } else {
              onPatch({ left_level: v });
            }
          }}
          options={ARCH_HEIGHT_OPTIONS}
          disabled={readOnly}
          placeholder="Select"
          className="min-w-[7rem]"
        />
        {data.left_level && data.left_level !== 'none' ? (
          <>
            <FieldLabel>Height</FieldLabel>
            <NumInput
              value={data.left_height_mm}
              onChange={(v) => onPatch({ left_height_mm: v })}
              min={1}
              max={9}
              disabled={readOnly}
              placeholder="1-9"
              suffix="mm"
              width="w-20"
              invalid={data.left_height_mm == null}
            />
            {data.left_height_mm == null ? (
              <span className="text-[11px] font-medium text-error-500">Required</span>
            ) : null}
          </>
        ) : null}
      </SideRow>
      <SideRow side="R">
        <SelectSm
          value={data.right_level}
          onChange={(v) => {
            if (!v || v === 'none') {
              onPatch({ right_level: v, right_height_mm: undefined });
            } else {
              onPatch({ right_level: v });
            }
          }}
          options={ARCH_HEIGHT_OPTIONS}
          disabled={readOnly}
          placeholder="Select"
          className="min-w-[7rem]"
        />
        {data.right_level && data.right_level !== 'none' ? (
          <>
            <FieldLabel>Height</FieldLabel>
            <NumInput
              value={data.right_height_mm}
              onChange={(v) => onPatch({ right_height_mm: v })}
              min={1}
              max={9}
              disabled={readOnly}
              placeholder="1-9"
              suffix="mm"
              width="w-20"
              invalid={data.right_height_mm == null}
            />
            {data.right_height_mm == null ? (
              <span className="text-[11px] font-medium text-error-500">Required</span>
            ) : null}
          </>
        ) : null}
      </SideRow>
    </div>
  );
}

// ---- Reinforcement column (used inside Arch reinforcement) --------------

interface ReinforcementColumnProps {
  label: string;
  leftValue?: boolean | undefined;
  rightValue?: boolean | undefined;
  onChange: (p: { left?: boolean | undefined; right?: boolean | undefined }) => void;
  readOnly?: boolean | undefined;
}

function ReinforcementColumn({
  label,
  leftValue,
  rightValue,
  onChange,
  readOnly,
}: ReinforcementColumnProps) {
  // Switched from a SelectSm dropdown to a YesNoToggle per side so this
  // card matches the medial/lateral flange layout right above it.
  return (
    <div className="space-y-2">
      <FieldLabel>{label}</FieldLabel>
      <SideRow side="L">
        <YesNoToggle
          value={leftValue ?? false}
          onChange={(b) => onChange({ left: b })}
          disabled={readOnly}
        />
      </SideRow>
      <SideRow side="R">
        <YesNoToggle
          value={rightValue ?? false}
          onChange={(b) => onChange({ right: b })}
          disabled={readOnly}
        />
      </SideRow>
    </div>
  );
}

// ---- Fascial accommodation row -------------------------------------------

function FascialRow({
  side,
  option,
  depth,
  onChange,
  readOnly,
}: {
  side: 'L' | 'R';
  option: FascialAccommodationOption | undefined;
  depth: number | undefined;
  onChange: (p: {
    option?: FascialAccommodationOption | undefined;
    depth?: number | undefined;
  }) => void;
  readOnly?: boolean | undefined;
}) {
  const showsDepth = option === 'yes' || option === 'yes_poron';
  return (
    <SideRow side={side}>
      <SelectSm
        value={option}
        onChange={(v) => {
          // Seed the default depth when the user flips to a Yes option from
          // None - feels nicer than them having to type -3 manually.
          if ((v === 'yes' || v === 'yes_poron') && depth == null) {
            onChange({ option: v, depth: FASCIAL_DEFAULT_DEPTH_MM });
          } else if (v === 'none') {
            onChange({ option: v, depth: undefined });
          } else {
            onChange({ option: v });
          }
        }}
        options={FASCIAL_OPTIONS}
        disabled={readOnly}
        placeholder="Select option"
        className="min-w-[8rem]"
      />
      {showsDepth ? (
        <>
          <FieldLabel>Depth</FieldLabel>
          <NumInput
            value={depth}
            onChange={(v) => onChange({ depth: v })}
            min={-6}
            max={-1}
            step={1}
            disabled={readOnly}
            placeholder="-3"
            suffix="mm"
            width="w-20"
          />
        </>
      ) : null}
    </SideRow>
  );
}

// ---- Yes/No toggle -------------------------------------------------------

function YesNoToggle({
  value,
  onChange,
  disabled,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean | undefined;
}) {
  return (
    <div className="inline-flex gap-1">
      {[
        { v: true, label: 'Yes' },
        { v: false, label: 'No' },
      ].map((opt) => (
        <button
          key={String(opt.v)}
          type="button"
          onClick={() => !disabled && onChange(opt.v)}
          disabled={disabled}
          className={cn(
            'px-3 py-1 rounded-md border text-xs transition',
            value === opt.v
              ? 'border-brand-500 bg-brand-50 text-brand-700 font-medium'
              : 'border-border-subtle hover:border-brand-300',
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
